import { getOfflineDb } from '../offline/dbFactory'
import { ensureOfflineReady } from '../offline/initOffline'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import {
  buildQueuedEntityMutationResult,
  type EntityBatchSyncBlocker,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  syncedEntityMutationResult,
  type EntityMutationResult,
  type EntityMutationSyncState
} from './entityMutationSync'
import {
  buildProductionUnitMetadata,
  readCurrentFarm,
  writeCurrentFarm,
  type WizardFarm,
  type WizardProductionUnit
} from './farmWizard'
import { purgeLocalDataForFarm } from './entityCachePurge'

export type EditFarmMutationResult = EntityMutationResult<WizardFarm>

export type DeleteFarmMutationResult = EntityMutationResult<undefined>

export type EditFarmSyncTarget = {
  farmId: string | null
  productionUnitIds: string[]
}

const isOnline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine

const summarizeOutboxMessages = (messages: Array<string | null>): string | null => {
  const unique = Array.from(
    new Set(
      messages
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    )
  )

  if (unique.length === 0) return null
  if (unique.length === 1) return unique[0]
  return `${unique[0]} (${unique.length - 1} more issue${unique.length === 2 ? '' : 's'})`
}

const mergeBlockers = (
  blockers: EntityBatchSyncBlocker[]
): EntityBatchSyncBlocker[] => {
  const blockersByKey = new Map<string, EntityBatchSyncBlocker>()

  blockers.forEach((blocker) => {
    const key = `${blocker.entityType}:${blocker.entityId}:${blocker.scope}`
    if (!blockersByKey.has(key)) {
      blockersByKey.set(key, blocker)
    }
  })

  return Array.from(blockersByKey.values())
}

const mergeBatchStatuses = (
  statuses: EntityBatchSyncStatus[]
): EntityBatchSyncStatus => {
  const meaningfulStatuses = statuses.filter((status) => status.syncState !== 'synced' || status.blockedCount > 0)
  if (meaningfulStatuses.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  const blockers = mergeBlockers(meaningfulStatuses.flatMap((status) => status.blockers))
  const blockedEntityIds = new Set(blockers.map((blocker) => `${blocker.entityType}:${blocker.entityId}`))

  return {
    syncState: meaningfulStatuses.some((status) => status.syncState === 'offline') ? 'offline' : 'pending',
    blockedCount: blockedEntityIds.size,
    errorMessage: summarizeOutboxMessages(meaningfulStatuses.map((status) => status.errorMessage)),
    blockers
  }
}

const normalizeFacilitySyncTarget = (
  target: EditFarmSyncTarget
): EditFarmSyncTarget => ({
  farmId: String(target.farmId ?? '').trim() || null,
  productionUnitIds: Array.from(
    new Set(target.productionUnitIds.map((id) => String(id ?? '').trim()).filter(Boolean))
  )
})

export const readEditFacilitySyncStatus = async (
  target: EditFarmSyncTarget
): Promise<EntityBatchSyncStatus> => {
  const normalizedTarget = normalizeFacilitySyncTarget(target)
  if (!normalizedTarget.farmId && normalizedTarget.productionUnitIds.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  const [farmStatus, productionUnitStatus] = await Promise.all([
    normalizedTarget.farmId
      ? readEntityBatchSyncStatus('farm', [normalizedTarget.farmId])
      : Promise.resolve({ syncState: 'synced', blockedCount: 0, errorMessage: null, blockers: [] } as EntityBatchSyncStatus),
    normalizedTarget.productionUnitIds.length > 0
      ? readEntityBatchSyncStatus('productionUnit', normalizedTarget.productionUnitIds)
      : Promise.resolve({ syncState: 'synced', blockedCount: 0, errorMessage: null, blockers: [] } as EntityBatchSyncStatus)
  ])

  return mergeBatchStatuses([farmStatus, productionUnitStatus])
}

export const retryEditFacilitySyncStatusWithReport = async (
  target: EditFarmSyncTarget,
  options?: { maxEntries?: number; maxPasses?: number }
): Promise<EntityBatchSyncRetryReport> => {
  const normalizedTarget = normalizeFacilitySyncTarget(target)
  if (!normalizedTarget.farmId && normalizedTarget.productionUnitIds.length === 0) {
    return {
      status: {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      },
      skippedNotFoundCount: 0
    }
  }

  const reports = await Promise.all([
    normalizedTarget.farmId
      ? retryEntityBatchSyncStatusWithReport({
        entityType: 'farm',
        entityIds: [normalizedTarget.farmId],
        maxEntries: options?.maxEntries,
        maxPasses: options?.maxPasses
      })
      : Promise.resolve({
        status: {
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        },
        skippedNotFoundCount: 0
      } as EntityBatchSyncRetryReport),
    normalizedTarget.productionUnitIds.length > 0
      ? retryEntityBatchSyncStatusWithReport({
        entityType: 'productionUnit',
        entityIds: normalizedTarget.productionUnitIds,
        maxEntries: options?.maxEntries,
        maxPasses: options?.maxPasses
      })
      : Promise.resolve({
        status: {
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        },
        skippedNotFoundCount: 0
      } as EntityBatchSyncRetryReport)
  ])

  return {
    status: mergeBatchStatuses(reports.map((report) => report.status)),
    skippedNotFoundCount: reports.reduce((total, report) => total + report.skippedNotFoundCount, 0)
  }
}

const readEditFarmSyncState = async (
  target: EditFarmSyncTarget
): Promise<EntityMutationSyncState> => {
  if (!target.farmId && target.productionUnitIds.length === 0) {
    return 'synced'
  }

  const trackedProductionUnitIds = new Set(
    target.productionUnitIds
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  )

  const entries = await getOfflineDb().entityOutbox.toArray()
  const hasPending = entries.some((entry) => {
    if (target.farmId && entry.entityType === 'farm' && entry.entityId === target.farmId) {
      return true
    }

    return entry.entityType === 'productionUnit' && trackedProductionUnitIds.has(entry.entityId)
  })

  return hasPending ? 'pending' : 'synced'
}

const buildGroupedFarmMutationResult = (
  farm: WizardFarm,
  target: EditFarmSyncTarget
): EditFarmMutationResult => {
  if (!target.farmId && target.productionUnitIds.length === 0) {
    return syncedEntityMutationResult(farm)
  }

  if (!isOnline()) {
    return {
      data: farm,
      syncState: 'offline',
      syncPromise: null
    }
  }

  const syncPromise = (async (): Promise<EntityMutationSyncState> => {
    try {
      await drainEntityOutboxNow({ maxEntries: 25, maxPasses: 5 })
    } catch (error) {
      console.warn('[editFarmApi] failed to drain entity outbox', error)
    }

    return readEditFarmSyncState(target)
  })()

  return {
    data: farm,
    syncState: 'pending',
    syncPromise
  }
}

const buildFarmPayload = (farm: WizardFarm) => ({
  id: farm.id,
  name: farm.name,
  altitude: farm.altitude,
  location: farm.location,
  metadata: farm.metadata
})

const buildProductionUnitPayload = (farmId: string, unit: WizardProductionUnit) => ({
  id: unit.id,
  farmId,
  name: unit.name,
  altitude: unit.altitude,
  metadata: buildProductionUnitMetadata(unit, unit)
})

const serialize = (value: unknown): string => JSON.stringify(value)

const enqueueChangedProductionUnits = async (
  farmId: string,
  previousUnits: WizardProductionUnit[],
  nextUnits: WizardProductionUnit[]
): Promise<Set<string>> => {
  const previousUnitsById = new Map(previousUnits.map((unit) => [String(unit.id), unit]))
  const nextUnitsById = new Map(nextUnits.map((unit) => [String(unit.id), unit]))
  const trackedProductionUnitIds = new Set<string>()

  for (const [unitId, previousUnit] of previousUnitsById.entries()) {
    if (nextUnitsById.has(unitId)) continue

    trackedProductionUnitIds.add(unitId)
    await enqueueEntityChange({
      entityType: 'productionUnit',
      op: 'delete',
      entityId: unitId,
      payload: {
        id: unitId,
        farmId,
        name: previousUnit.name,
        altitude: previousUnit.altitude,
        metadata: previousUnit.metadata
      }
    })
  }

  for (const nextUnit of nextUnits) {
    const unitId = String(nextUnit.id ?? '').trim()
    if (!unitId) continue

    const previousUnit = previousUnitsById.get(unitId)
    const nextUnitPayload = buildProductionUnitPayload(farmId, nextUnit)
    const previousUnitPayload = previousUnit ? buildProductionUnitPayload(farmId, previousUnit) : null

    if (previousUnitPayload && serialize(nextUnitPayload) === serialize(previousUnitPayload)) {
      continue
    }

    trackedProductionUnitIds.add(unitId)
    await enqueueEntityChange({
      entityType: 'productionUnit',
      op: 'upsert',
      entityId: unitId,
      payload: nextUnitPayload
    })
  }

  return trackedProductionUnitIds
}

const syncCurrentFarmSnapshot = (farm: WizardFarm | null): void => {
  const currentFarm = readCurrentFarm()
  if (!currentFarm) return

  if (!farm) {
    if (currentFarm.id) {
      writeCurrentFarm(null)
    }
    return
  }

  if (currentFarm.id === farm.id) {
    writeCurrentFarm(farm)
  }
}

export const saveEditedFarm = async (
  nextFarm: WizardFarm,
  previousFarm?: WizardFarm | null
): Promise<EditFarmMutationResult> => {
  await ensureOfflineReady()

  const farmId = String(nextFarm.id ?? '').trim()
  if (!farmId) {
    throw new Error('Facility is required before saving changes')
  }

  const baseFarm = previousFarm ?? await dexieFarmRepo.getById(farmId)
  await dexieFarmRepo.upsert(nextFarm)

  const nextFarmPayload = buildFarmPayload(nextFarm)
  const previousFarmPayload = baseFarm ? buildFarmPayload(baseFarm) : null
  const farmChanged = !previousFarmPayload || serialize(nextFarmPayload) !== serialize(previousFarmPayload)

  if (farmChanged) {
    await enqueueEntityChange({
      entityType: 'farm',
      op: 'upsert',
      entityId: farmId,
      payload: nextFarmPayload
    })
  }

  const previousUnits = Array.isArray(baseFarm?.productionUnits) ? baseFarm.productionUnits : []
  const nextUnits = Array.isArray(nextFarm.productionUnits) ? nextFarm.productionUnits : []
  const trackedProductionUnitIds = await enqueueChangedProductionUnits(farmId, previousUnits, nextUnits)

  syncCurrentFarmSnapshot(nextFarm)

  return buildGroupedFarmMutationResult(nextFarm, {
    farmId: farmChanged ? farmId : null,
    productionUnitIds: Array.from(trackedProductionUnitIds)
  })
}

export const deleteEditedFarm = async (farm: WizardFarm): Promise<DeleteFarmMutationResult> => {
  await ensureOfflineReady()
  // Purge the farm's own local lot/event-log caches BEFORE removing the
  // farm row — a farm delete cascades server-side through every Lot,
  // FeedLog, HarvestLog, LotCostEvent, WaterLog, MortalityLog,
  // WeightSampleLog, and FishStockLog underneath it, but the local Dexie
  // cache is never told to follow along (see entityCachePurge.ts).
  await purgeLocalDataForFarm(farm.id, (farm.productionUnits ?? []).map((pu) => ({ id: pu.id, name: pu.name })))
  await dexieFarmRepo.delete(farm.id)
  syncCurrentFarmSnapshot(null)

  await enqueueEntityChange({
    entityType: 'farm',
    op: 'delete',
    entityId: farm.id,
    payload: { id: farm.id, name: farm.name }
  })

  return buildQueuedEntityMutationResult(undefined, {
    entityType: 'farm',
    entityId: farm.id,
    maxEntries: 10,
    maxPasses: 5
  })
}